import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, Loader2, XCircle, Crown } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { API } from '../config/api';

export default function MembershipSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { updateUser } = useAuth();
  const [status, setStatus] = useState('checking');
  const [attempts, setAttempts] = useState(0);
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    } else {
      setStatus('error');
    }
  }, [sessionId]);

  const pollPaymentStatus = async () => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const response = await axios.get(`${API}/payments/status/${sessionId}`);
      
      if (response.data.payment_status === 'paid') {
        setStatus('success');
        updateUser({ is_member: true });
        return;
      } else if (response.data.status === 'expired') {
        setStatus('expired');
        return;
      }

      setAttempts(prev => prev + 1);
      setTimeout(pollPaymentStatus, pollInterval);
    } catch (error) {
      console.error('Payment status error:', error);
      setAttempts(prev => prev + 1);
      setTimeout(pollPaymentStatus, pollInterval);
    }
  };

  return (
    <div className="min-h-screen py-16 px-4 flex items-center justify-center">
      <div className="max-w-md text-center">
        {status === 'checking' && (
          <>
            <Loader2 className="mx-auto text-[#D4AF37] animate-spin mb-6" size={48} />
            <h1 className="font-serif text-2xl text-[#F5F5F0] mb-4">Processing Membership...</h1>
            <p className="text-[#A1A1AA]">Please wait while we activate your membership.</p>
          </>
        )}

        {status === 'success' && (
          <>
            <Crown className="mx-auto text-[#D4AF37] mb-6" size={48} />
            <h1 className="font-serif text-2xl text-[#F5F5F0] mb-4">Welcome, Collector!</h1>
            <p className="text-[#A1A1AA] mb-8">
              Your membership is now active. You can now purchase any artwork 
              from our gallery of authentic human-created pieces.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/gallery" className="btn-primary">
                Start Collecting
              </Link>
            </div>
          </>
        )}

        {(status === 'error' || status === 'expired' || status === 'timeout') && (
          <>
            <XCircle className="mx-auto text-[#EF4444] mb-6" size={48} />
            <h1 className="font-serif text-2xl text-[#F5F5F0] mb-4">
              {status === 'timeout' ? 'Activation Timeout' : 'Membership Failed'}
            </h1>
            <p className="text-[#A1A1AA] mb-8">
              {status === 'timeout' 
                ? 'We couldn\'t verify your payment. Please check your email for confirmation.'
                : 'There was an issue activating your membership. Please try again.'}
            </p>
            <Button onClick={() => navigate('/membership')} className="btn-primary">
              Try Again
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
